/**
 * PushOnboardingBanner — dezenter Banner über dem Eingabefeld
 *
 * Wird nach erster gesendeter User-Nachricht gezeigt (über shouldShowBanner-Logik
 * im Hook). Aktivieren-Button löst Browser-Permission-Dialog aus.
 *
 * Drei Varianten je nach Kontext:
 *   1. Standard: "Antwort sofort erhalten? Wir benachrichtigen dich."
 *      → Erlauben / Später
 *   2. iOS aber nicht im PWA-Mode: Hinweis zum Home-Bildschirm-Hinzufügen
 *      → "Verstanden" (=Snooze)
 *   3. Permission denied: Hinweis Browser-Einstellungen
 *      → "Verstanden" (=Snooze)
 */

import React from 'react';
import { Bell, X } from 'lucide-react';
import { usePushOnboarding } from '../../hooks/usePushOnboarding';

interface PushOnboardingBannerProps {
  enabled: boolean;
  /** Wenn true: 14-Tage-Snooze ignorieren (Admin-Bereich, Setup-Pflicht). */
  ignoreSnooze?: boolean;
}

export function PushOnboardingBanner({ enabled, ignoreSnooze }: PushOnboardingBannerProps): React.ReactElement | null {
  const { shouldShowBanner, capability, enable, snooze, error, isWorking } = usePushOnboarding({
    enabled,
    ...(ignoreSnooze !== undefined && { ignoreSnooze })
  });

  // Spezial-Variante: iOS aber nicht im PWA-Mode → Hinweis statt Aktivier-Button
  const showIOSPwaHint = enabled && capability.isIOS && !capability.isStandalone && !shouldShowBanner === false;
  // Logik: Banner zeigen entweder bei normalem Onboarding oder bei iOS-PWA-Hinweis,
  // aber nur wenn Push überhaupt unterstützt wird (sonst gar nichts zeigen)
  const showStandard = shouldShowBanner && !(capability.isIOS && !capability.isStandalone);
  const showIOSHint = shouldShowBanner && capability.isIOS && !capability.isStandalone;

  if (!showStandard && !showIOSHint) {
    return null;
  }

  return (
    <div
      role="status"
      className="
        glass-surface-soft
        rounded-xl
        px-3 py-3
        mx-3 mb-3
        font-mono
        flex items-start gap-3
      "
    >
      <Bell className="w-4 h-4 text-brand mt-0.5 shrink-0" strokeWidth={2} aria-hidden="true" />

      <div className="flex-1 min-w-0">
        {showIOSHint ? (
          <>
            <p className="text-[12px] leading-snug">
              Antwort sofort auf dem Sperrbildschirm erhalten?
            </p>
            <p className="text-[11px] opacity-60 leading-snug mt-0.5">
              Tippe in Safari auf das Teilen-Symbol und wähle „Zum Home-Bildschirm“. Dann sind Benachrichtigungen aktivierbar.
            </p>
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                onClick={snooze}
                className="
                  text-[11px] px-3 py-1.5 rounded-lg
                  border border-white/10 dark:border-white/10
                  hover:bg-white/5 dark:hover:bg-white/5
                  transition-colors duration-150
                "
              >
                Verstanden
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="text-[12px] leading-snug">
              <span className="font-semibold">Antwort sofort erhalten?</span>{' '}
              Wir benachrichtigen dich, sobald RENDITEFUCHS antwortet.
            </p>
            {error && (
              <p className="text-[11px] text-red-400 mt-1">{error}</p>
            )}
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                onClick={enable}
                disabled={isWorking}
                className="
                  text-[11px] px-3 py-1.5 rounded-lg
                  bg-brand text-gray-950 font-semibold
                  hover:bg-brand-hover
                  transition-colors duration-150
                  disabled:opacity-50 disabled:cursor-not-allowed
                "
              >
                {isWorking ? 'Wird aktiviert…' : 'Aktivieren'}
              </button>
              <button
                type="button"
                onClick={snooze}
                disabled={isWorking}
                className="
                  text-[11px] px-3 py-1.5 rounded-lg
                  border border-white/10 dark:border-white/10
                  hover:bg-white/5 dark:hover:bg-white/5
                  transition-colors duration-150
                  disabled:opacity-50
                "
              >
                Später
              </button>
            </div>
          </>
        )}
      </div>

      <button
        type="button"
        onClick={snooze}
        aria-label="Banner schließen"
        className="
          shrink-0
          opacity-40 hover:opacity-80
          transition-opacity duration-150
        "
      >
        <X className="w-3.5 h-3.5" strokeWidth={2} />
      </button>
    </div>
  );
}
